'use client';

import { useEffect, useCallback, useMemo } from 'react';
import ConcernAndTreatmentsSection from '@/features/aesthetic/components/ConcernAndTreatmentsSection';
import { usePharmacyBookingModal } from '@/features/pharamacy/components/PharmacyBookingModalContext/PharmacyBookingModalContext';
import {
  usePharmacyBookingStore,
  type PharmacyBookingDisplayContext,
} from '@/features/pharamacy/store/usePharmacyBookingStore';
import type { WhatIsYourConcern } from '@/types/components/whatIsYourConcern';

interface Props {
  data: WhatIsYourConcern;
}

function buildDisplayContextMap(
  data: WhatIsYourConcern,
): Record<number, PharmacyBookingDisplayContext> {
  const map: Record<number, PharmacyBookingDisplayContext> = {};

  for (const card of data.cards) {
    const cardAny = card as unknown as Record<string, unknown>;
    const catImage = cardAny.image as
      { urlDesktop?: string; urlMobile?: string; alt?: string } | undefined;

    for (const treatment of card.treatments || []) {
      const treatAny = treatment as unknown as Record<string, unknown>;
      const serviceId = treatAny.serviceId as number | undefined;

      if (serviceId === undefined) continue;

      const match = treatment.heading.content.match(/\*\*(.+?)\*\*/);
      const cleanedName = match ? match[1] : treatment.heading.content;

      const trtImage = treatAny.image as
        | {
            urlDesktop?: string;
            urlMobile?: string;
            alt?: string;
            width?: number;
            height?: number;
            className?: string;
          }
        | undefined;

      map[serviceId] = {
        categoryName: card.heading.content,
        categoryImage: catImage
          ? {
              urlDesktop: catImage.urlDesktop,
              urlMobile: catImage.urlMobile,
              alt: catImage.alt,
            }
          : undefined,
        treatmentName: cleanedName,
        treatmentImage: trtImage
          ? {
              urlDesktop: trtImage.urlDesktop,
              urlMobile: trtImage.urlMobile,
              alt: trtImage.alt,
              width: trtImage.width,
              height: trtImage.height,
              className: trtImage.className,
            }
          : undefined,
      };
    }
  }

  return map;
}

function findDisplayContextByServiceId(
  data: WhatIsYourConcern,
  serviceId: number,
): PharmacyBookingDisplayContext | null {
  for (const card of data.cards) {
    const cardAny = card as unknown as Record<string, unknown>;
    const catImage = cardAny.image as
      { urlDesktop?: string; urlMobile?: string; alt?: string } | undefined;

    for (const treatment of card.treatments || []) {
      const treatAny = treatment as unknown as Record<string, unknown>;
      if (treatAny.serviceId === serviceId) {
        const match = treatment.heading.content.match(/\*\*(.+?)\*\*/);
        const cleanedName = match ? match[1] : treatment.heading.content;

        const trtImage = treatAny.image as
          | {
              urlDesktop?: string;
              urlMobile?: string;
              alt?: string;
              width?: number;
              height?: number;
              className?: string;
            }
          | undefined;

        return {
          categoryName: card.heading.content,
          categoryImage: catImage
            ? {
                urlDesktop: catImage.urlDesktop,
                urlMobile: catImage.urlMobile,
                alt: catImage.alt,
              }
            : undefined,
          treatmentName: cleanedName,
          treatmentImage: trtImage
            ? {
                urlDesktop: trtImage.urlDesktop,
                urlMobile: trtImage.urlMobile,
                alt: trtImage.alt,
                width: trtImage.width,
                height: trtImage.height,
                className: trtImage.className,
              }
            : undefined,
        };
      }
    }
  }
  return null;
}

function getPharmacyCategoryKey(name: string): string {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/[\u2018’\u02BB\u02BC\uFF07]/g, "'")
    .replace(/\s+/g, ' ');

  if (normalized === 'general health') return 'general-health';
  if (normalized === "women's health") return 'womens-health';
  if (normalized === "men's health") return 'mens-health';
  if (normalized === 'personal care') return 'personal-care';
  return '';
}

function buildCategoryDisplayContextMap(
  data: WhatIsYourConcern,
): Record<string, PharmacyBookingDisplayContext> {
  const map: Record<string, PharmacyBookingDisplayContext> = {};

  for (const card of data.cards) {
    const cardAny = card as unknown as Record<string, unknown>;
    const key = getPharmacyCategoryKey(card.heading.content);
    if (!key) continue;

    const catImage = cardAny.image as
      { urlDesktop?: string; urlMobile?: string; alt?: string } | undefined;

    map[key] = {
      categoryName: card.heading.content,
      categoryImage: catImage
        ? {
            urlDesktop: catImage.urlDesktop,
            urlMobile: catImage.urlMobile,
            alt: catImage.alt,
          }
        : undefined,
    };
  }

  return map;
}

function handleBookingLinkClick(
  anchor: HTMLAnchorElement,
  data: WhatIsYourConcern,
  setDisplayContext: (context: PharmacyBookingDisplayContext | null) => void,
): number | undefined {
  const href = anchor.getAttribute('href');
  if (!href) return undefined;

  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return undefined;
  }

  if (url.pathname !== '/pharmacy/book') return undefined;

  const rawServiceId = url.searchParams.get('serviceId');
  const serviceId = rawServiceId ? Number(rawServiceId) : undefined;

  if (serviceId !== undefined) {
    const context = findDisplayContextByServiceId(data, serviceId);
    setDisplayContext(context);
  } else {
    setDisplayContext(null);
  }

  return serviceId;
}

export default function PharmacistAdvicesWithModal({ data }: Props) {
  const { openModal } = usePharmacyBookingModal();
  const setDisplayContext = usePharmacyBookingStore((s) => s.setDisplayContext);
  const setDisplayContextMap = usePharmacyBookingStore(
    (s) => s.setDisplayContextMap,
  );
  const setCategoryDisplayContextMap = usePharmacyBookingStore(
    (s) => s.setCategoryDisplayContextMap,
  );

  const contextMap = useMemo(() => buildDisplayContextMap(data), [data]);
  const catContextMap = useMemo(
    () => buildCategoryDisplayContextMap(data),
    [data],
  );

  useEffect(() => {
    setDisplayContextMap(contextMap);
    setCategoryDisplayContextMap(catContextMap);
  }, [
    contextMap,
    catContextMap,
    setDisplayContextMap,
    setCategoryDisplayContextMap,
  ]);

  const handleBookingClickCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;
      const serviceId = handleBookingLinkClick(
        anchor as HTMLAnchorElement,
        data,
        setDisplayContext,
      );
      if (serviceId === undefined) return;
      e.preventDefault();
      e.stopPropagation();
      openModal(serviceId);
    },
    [data, setDisplayContext, openModal],
  );

  const handleKeyDownCapture = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter') return;
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;
      const serviceId = handleBookingLinkClick(
        anchor as HTMLAnchorElement,
        data,
        setDisplayContext,
      );
      if (serviceId === undefined) return;
      e.preventDefault();
      e.stopPropagation();
      openModal(serviceId);
    },
    [data, setDisplayContext, openModal],
  );

  return (
    <div
      onClickCapture={handleBookingClickCapture}
      onKeyDownCapture={handleKeyDownCapture}
      id="pharmacist_advices_treatment_section"
    >
      <ConcernAndTreatmentsSection
        data={data}
        concernOverlayClassName="absolute bottom-0 left-0 right-0 rounded-b-[10px] bg-linear-to-t from-black/70 via-black/40 to-transparent pt-12 pb-4 px-4 text-left font-normal text-white"
      />
    </div>
  );
}
